import { EventSourcePolyfill } from 'event-source-polyfill';
import ItemClasificacion from './ItemClasificacion.js';
import SseCompartido from './SseCompartido.js';
import { baseSse } from './sseBase.js';
import AvisoUnicoDeError from '../utils/avisoUnicoDeError.js';

// Stream SSE genérico de core (/eventos/events): recibe eventos dirigidos al usuario, en
// concreto "error-servidor" (emitido por GlobalDefaultExceptionHandler). Muestra el mensaje en una
// banda y, bajo "+ detalles", qué puede hacer la persona y la referencia del error.
//
// #765 · La banda NO enseña la traza de Java, ni a ADMIN: nombres de clase, ficheros y líneas no le
// sirven a quien lo lee y enseñan la estructura interna de la aplicación. La API ya no la manda
// (`ErrorServidorSinTrazaAspect`); si una versión anterior la mandara, aquí se ignora igual. Lo que
// sirve para encontrar el fallo es la referencia, que el servidor escribe en su registro junto a la
// traza. Y la decisión de si se pinta la banda o el modal de la pantalla es de `avisoUnicoDeError`:
// el mismo error no sale dos veces.

let canal = null;
let f7ref = null;
let detalleAbierto = null;

function readToken() {
    try {
        const session = JSON.parse(localStorage.getItem('session') || '{}');
        return session.access_token || session.token || session.accessToken || '';
    } catch (e) {
        return '';
    }
}

function buildUrl() {
    return baseSse(ItemClasificacion.server_movilidad) + ItemClasificacion.prefix + 'eventos/events';
}

function escapeHtml(s) {
    return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function mostrarDetalle(textoHtml, referencia) {
    if (!f7ref) return;
    const html =
        '<div class="popup error-stack-popup">' +
        '  <div class="page">' +
        '    <div class="navbar"><div class="navbar-bg"></div><div class="navbar-inner">' +
        '      <div class="title">Detalle del error</div>' +
        '      <div class="right"><a class="link popup-close">Cerrar</a></div>' +
        '    </div></div>' +
        '    <div class="page-content" style="padding:12px;">' +
        '      <p style="font-weight:600;color:#c00;">' + textoHtml + '</p>' +
        '      <p>Si no sabes cómo resolverlo, avisa a soporte y dales esta referencia: con ella localizan el error en el registro del servidor.</p>' +
        '      <p>Referencia: <b class="error-sse-referencia" style="font-family:monospace;font-size:15px;user-select:all;">' + escapeHtml(referencia) + '</b></p>' +
        '    </div>' +
        '  </div>' +
        '</div>';
    const popup = f7ref.popup.create({ content: html, on: { closed() {
        if (detalleAbierto === popup) detalleAbierto = null;
        popup.destroy();
    } } });
    detalleAbierto = popup;
    popup.open();
}

/**
 * Pinta la banda y la devuelve abierta. `deLaPantalla` es el texto del modal que ha cedido su
 * sitio: la pantalla lo habría pintado como HTML en su diálogo y así se pinta aquí; el mensaje que
 * llega del servidor, en cambio, se escapa.
 */
function pintarBanda(texto, referencia, deLaPantalla) {
    if (!f7ref) return null;
    const textoHtml = deLaPantalla ? String(texto) : escapeHtml(texto);
    const toast = f7ref.toast.create({
        text: '<span class="error-sse-msg">⚠️ ' + textoHtml + '</span>' +
              (referencia ? ' <a class="link error-sse-detalles" style="color:#fff;text-decoration:underline;">+ detalles</a>' : ''),
        horizontalPosition: 'center',
        closeButton: true,
        // Lo que tarda en leerse: la banda puede llevar ahora el texto entero del modal que ha cedido.
        closeTimeout: Math.min(20000, Math.max(9000, String(texto).length * 60)),
        cssClass: 'error-sse-toast'
    });
    toast.open();
    if (referencia && toast.el) {
        const link = toast.el.querySelector('.error-sse-detalles');
        if (link) {
            link.addEventListener('click', function () {
                toast.close();
                mostrarDetalle(textoHtml, referencia);
            });
        }
    }
    return toast;
}

function ensureConnected() {
    if (canal) return;
    // Canal compartido entre pestañas: solo la pestaña líder abre la SSE real.
    canal = SseCompartido.conectar('errores', {
        abrir: function (emitir) {
            const eventSource = new EventSourcePolyfill(buildUrl(), {
                headers: { Authorization: 'Bearer ' + readToken() },
                heartbeatTimeout: 1800000
            });
            // Evento de negocio (renombrado para NO colisionar con el evento nativo 'error' de EventSource).
            eventSource.addEventListener('error-servidor', function (event) {
                let payload = null;
                try { payload = JSON.parse(event.data); } catch (e) { payload = { message: event.data }; }
                emitir(payload);
            });
            eventSource.addEventListener('error', function (err) {
                console.warn('[EventosErrorEvents] SSE conexión', err);
            });
            return eventSource;
        },
        onEvento: AvisoUnicoDeError.desdeServidor,
        // Un error viejo no debe re-mostrarse al abrir una pestaña nueva.
        reenviarUltimo: false
    });
}

export default {
    /** Arranca el stream de errores (idempotente). Pasa el f7 para mostrar la banda y el detalle. */
    start(f7) {
        if (f7) {
            f7ref = f7;
            AvisoUnicoDeError.instalar(f7, pintarBanda);
        }
        ensureConnected();
    },
    /**
     * Al cerrar sesión. Desconectar el canal no basta: lo que ya estaba en camino —una banda a la
     * espera, un modal que había cedido, el detalle abierto— es de quien se ha ido, y saldría en
     * la pantalla de acceso o en la sesión del siguiente.
     */
    stop() {
        if (canal) {
            canal.desconectar();
            canal = null;
        }
        AvisoUnicoDeError.olvidar();
        if (detalleAbierto) detalleAbierto.close();
    }
};
